import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import CountUp from "@/components/CountUp";
import AnimatedBackdrop from "@/components/AnimatedBackdrop";

const SHYFT_MAP = "https://customer-assets.emergentagent.com/job_luxe-med-spa/artifacts/kfin0oie_IMG_1265.jpg";

export default function Integration() {
  const [form, setForm] = useState({ name: "", email: "", business: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    toast.success("Consultation request received. A concierge will be in touch within 24 hours.");
    setForm({ name: "", email: "", business: "", message: "" });
  };

  return (
    <div data-testid="integration-page" className="pt-32">
      {/* HERO */}
      <section className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-8 overflow-hidden">
        <AnimatedBackdrop variant="cream" />
        <div className="relative z-10">
          <div className="overline">White-Glove Integration</div>
          <h1 className="font-serif-editorial text-5xl lg:text-8xl mt-6 leading-none max-w-4xl">
            Not equipment. <em className="italic">An installed revenue line.</em>
          </h1>
          <p className="mt-8 text-lg lg:text-xl leading-relaxed text-[#5C5C5C] max-w-2xl">
            Every TheraTec order arrives with a concierge — coordinated crating, certified installation, staff training, and a protocol library. You buy equipment. You receive a bookable service line.
          </p>
        </div>
      </section>

      {/* SHYFT PARTNERSHIP */}
      <section className="bg-[#0A0908] text-[#FAF9F6] mt-24 lg:mt-32 py-24 lg:py-32 overflow-hidden relative">
        {/* Ambient champagne glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[140px]" style={{ background: "radial-gradient(circle, #C9A961 0%, transparent 65%)" }} />
          <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] rounded-full opacity-15 blur-[130px]" style={{ background: "radial-gradient(circle, #E8B87A 0%, transparent 65%)" }} />
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
          {/* Partnership headline */}
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-[#C9A961]" />
              <div className="overline text-[#C9A961]">Nationwide White-Glove Delivery · Shyft Global Services</div>
            </div>
            <h2 className="font-serif-editorial text-4xl lg:text-6xl leading-[1.05]">
              White-glove delivery, <em className="italic">nationwide.</em><br />
              International, <em className="italic">on request.</em>
            </h2>
            <p className="mt-8 text-base lg:text-lg text-[#FAF9F6]/75 max-w-2xl leading-relaxed">
              Every chamber, table, and platform ships through <strong className="text-[#FAF9F6] font-medium">Shyft Global Services</strong> — our fulfillment partner and one of the most extensive medical field-service networks in North America. <span className="text-[#C9A961]">99% coverage of the US and Canada population</span>, expanded coverage globally through an extensive partner network supplemented with badged resources.
            </p>
          </div>

          {/* Stats row */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 border-t border-[#333] pt-12">
            {[
              { n: "99%", l: "Coverage of US & Canada population" },
              { n: "133+", l: "NA forward stocking locations" },
              { n: "Global", l: "Partner network + badged resources" },
              { n: "24h", l: "Certified technician dispatch" },
            ].map((s) => (
              <div key={s.n}>
                <div className="font-serif-editorial text-5xl lg:text-6xl text-[#FAF9F6]"><CountUp to={s.n} /></div>
                <div className="mt-3 text-xs text-[#A8A096] leading-relaxed max-w-[180px]">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Shyft coverage map — sized & centered */}
          <figure className="mt-20 mx-auto max-w-3xl" data-testid="shyft-map-figure">
            <div className="relative overflow-hidden border border-[#333] group">
              <img
                src={SHYFT_MAP}
                alt="Shyft North America Field Services Technicians coverage map — 99% US and Canada population coverage, 133+ forward stocking locations"
                className="w-full h-auto max-h-[420px] object-contain block bg-[#0A0908] group-hover:scale-[1.02] transition-transform duration-[1500ms] ease-out"
              />
            </div>
            <figcaption className="mt-6 text-center max-w-2xl mx-auto">
              <div className="overline text-[#C9A961] mb-3">Fulfillment Partner · Shyft Global Services</div>
              <p className="font-serif-editorial text-2xl lg:text-3xl leading-tight text-[#FAF9F6]">
                We deliver <em className="italic">nationwide white-glove service</em> in every US and Canadian market — and <em className="italic">international</em> on request.
              </p>
              <p className="mt-6 text-sm text-[#A8A096] leading-relaxed">
                Every dot on the map is a certified technician within dispatch range of your suite. From crate to calibration, your equipment is escorted the entire way.
              </p>
            </figcaption>
          </figure>

          {/* Feature checklist */}
          <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-6 border-t border-[#333] pt-12">
            {[
              "Nationwide white-glove crated shipping",
              "In-room delivery and setup — no dock drop-offs",
              "133+ North American forward stocking locations",
              "99% US & Canada population coverage",
              "Extensive partner network with badged resources",
              "International freight forwarding available on request",
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 size={18} strokeWidth={1.25} className="text-[#C9A961] mt-1 shrink-0" />
                <span className="text-sm text-[#FAF9F6]/85">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-STEP PROCESS */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-3 gap-12 border-b border-[#E5E5E5]">
        {[
          { n: "01", t: "Discovery", d: "Video walk-through of your suite. Bay planning, utility review, revenue projections." },
          { n: "02", t: "Delivery & Install", d: "Shyft certified crew handles crating, in-room delivery, installation, calibration, and cosmetic sign-off." },
          { n: "03", t: "Enablement", d: "On-site staff training, protocol library, marketing playbook, and 24-hour service." },
        ].map((s) => (
          <div key={s.n} className="border-t border-[#1C1C1C] pt-8">
            <div className="overline">{s.n}</div>
            <div className="font-serif-editorial text-3xl mt-4">{s.t}</div>
            <p className="mt-6 text-[#5C5C5C] leading-relaxed">{s.d}</p>
          </div>
        ))}
      </section>

      {/* CONSULTATION FORM */}
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
        <div className="overline">Request a Consultation</div>
        <h2 className="font-serif-editorial text-4xl mt-4">Tell us about your suite.</h2>
        <form onSubmit={submit} className="mt-12 space-y-8" data-testid="integration-form">
          <input data-testid="integ-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tt-input" />
          <input data-testid="integ-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="tt-input" />
          <input data-testid="integ-business" required placeholder="Med spa / business" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} className="tt-input" />
          <textarea data-testid="integ-message" required placeholder="Tell us what you're integrating…" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="tt-input resize-none" />
          <button data-testid="integ-submit" className="tt-btn-primary">Request Consultation</button>
        </form>
      </section>
    </div>
  );
}
